import { BarChart } from '@mui/icons-material';
import { StoneXNumber } from '../../../StoneXMui';
import { decileMatrixStyles, getDecileTextColor, readDecileColor } from '../DecileMatrixStyles';
import { MatrixBand } from '../HedgePlans/HedgePlanMatrixBands';

interface SeasonalMatrixPriceTableProps {
  columnLabel: string;
  mean: number;
  median: number | undefined;
  bands: MatrixBand[];
  // The band holding the price under the cursor on the chart, or else the last close.
  highlightedPercentile: number | undefined;
  // Bands kept on the chart.
  pinnedPercentiles: Set<number>;
  onBandClick: (band: MatrixBand) => void;
  // Previews a band on the chart without pinning it; cleared by onBandHoverEnd.
  onBandHover: (band: MatrixBand) => void;
  onBandHoverEnd: () => void;
}

export default function SeasonalMatrixPriceTable(props: SeasonalMatrixPriceTableProps) {
  const { columnLabel, mean, median, bands, highlightedPercentile, pinnedPercentiles, onBandClick, onBandHover, onBandHoverEnd } = props;

  return (
    <div style={{ ...decileMatrixStyles.container, maxWidth: '280px' }}>
      <h3 style={decileMatrixStyles.heading}>Price Matrix</h3>
      <table style={decileMatrixStyles.table}>
        <tbody>
          <tr>
            <td></td>
            <td style={{ ...decileMatrixStyles.headerCell, textAlign: 'right' }}>{columnLabel}</td>
          </tr>
          <tr>
            <td style={decileMatrixStyles.labelCell}>Mean</td>
            <td style={decileMatrixStyles.valueCell}><StoneXNumber number={mean} decimals={4} /></td>
          </tr>
          <tr>
            <td style={decileMatrixStyles.labelCell}>Median</td>
            <td style={decileMatrixStyles.valueCell}><StoneXNumber number={median} decimals={4} /></td>
          </tr>
          {bands.map((band) => {
            const isHighlighted = highlightedPercentile === band.percentile;
            const isPinned = pinnedPercentiles.has(band.percentile);
            const bandColor = readDecileColor(band.percentile);
            const bandTextColor = getDecileTextColor(band.percentile);

            return (
              <tr key={band.percentile}>
                <td style={{ ...decileMatrixStyles.labelCell, backgroundColor: bandColor, color: bandTextColor }}>{band.label}</td>
                <td
                  onClick={() => onBandClick(band)}
                  onMouseEnter={() => onBandHover(band)}
                  onMouseLeave={onBandHoverEnd}
                  style={{
                    ...decileMatrixStyles.valueCell,
                    backgroundColor: isHighlighted ? bandColor : 'transparent',
                    color: isHighlighted ? bandTextColor : 'var(--font-color)',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                    {isPinned && <BarChart style={{ fontSize: '16px', color: isHighlighted ? bandTextColor : bandColor }} />}
                    {`${band.lowValue.toFixed(3)} - ${band.highValue.toFixed(3)}`}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: 'var(--helper-text-color)' }}>
        The highlighted band holds the last close. Hover a cell or the chart&apos;s price line to preview a band on the chart; click a cell to keep it.
      </p>
    </div>
  );
}
